'use client';

import {
  BonusFilled,
  Close,
  ErrorFilled,
  InformationFilled,
  SuccessFilled,
  WarningFilled
} from '@ps/web-fusion-icons';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { useId, useState } from 'react';

import Icon from '../Icon/Icon';
import IconButton from '../IconButton/IconButton';
import { useFusionTheme, useIntl } from '../hooks';
import { TXTCLI_Close } from '../translations';

import styles from './InlineMessage.module.scss';

export const messageVariants = cva(styles.container, {
  variants: {
    variant: {
      info: styles.variantInfo,
      warning: styles.variantWarning,
      error: styles.variantError,
      success: styles.variantSuccess,
      bonus: styles.variantBonus
    }
  }
});

type MessageVariant = Exclude<VariantProps<typeof messageVariants>['variant'], null | undefined>;

export type InlineMessageProps = VariantProps<typeof messageVariants> & {
  variant?: MessageVariant;
  className?: string;
  title?: string;
  body: string;
  isDismissible: boolean;
  visible?: boolean;
  'aria-label'?: string;
};

const iconMap = {
  info: InformationFilled,
  warning: WarningFilled,
  error: ErrorFilled,
  success: SuccessFilled,
  bonus: BonusFilled
} as const satisfies Record<MessageVariant, React.FC<React.SVGProps<SVGElement>>>;

const InlineMessage = ({
  variant = 'info',
  className,
  title,
  body,
  isDismissible = true,
  visible = true,
  'aria-label': ariaLabel
}: InlineMessageProps) => {
  const { addThemeClassName } = useFusionTheme();
  const { formatMessage } = useIntl();
  const descriptionId = useId();
  const [isVisible, setVisible] = useState(visible);

  return (
    isVisible && (
      <div
        aria-label={ariaLabel ?? title ?? body}
        aria-describedby={descriptionId}
        className={addThemeClassName(messageVariants({ variant }), className)}
      >
        <Icon className={styles.icon} icon={iconMap[variant]} aria-hidden />
        <div className={styles.message}>
          {title && <h3 className={styles.title}>{title}</h3>}
          <div id={descriptionId} className={styles.body}>
            {body}
          </div>
        </div>
        {isDismissible && (
          <IconButton
            className={styles.close}
            icon={Close}
            size="md"
            aria-label={formatMessage(TXTCLI_Close)}
            onClick={() => setVisible(false)}
          />
        )}
      </div>
    )
  );
};

export default InlineMessage;
